/**
 * Tải dữ liệu cân bằng từ src/data/*.json thành DuLieuGame.
 * JSON import tĩnh lúc build — core vẫn thuần TS, không DOM.
 */
import type {
  MucChinhSachRiengTu,
  AmThanhMau,
  Cosmetic,
  DuLieuGame,
  HoiThoai,
  NhiemVuHangNgay,
  NoiDungCosmetic,
  NoiDungNhiemVu,
  NoiDungNpc,
  NoiDungSuuTap,
  NoiDungThanhTuu,
  NpcThamSo,
  ThanhTuu,
} from './types.js';
import thamSo from '../data/tham-so.json';
import thamSoMoPhong from '../data/tham-so-mo-phong.json';
import nhomHang from '../data/nhom-hang.json';
import matHang from '../data/mat-hang.json';
import khach from '../data/khach.json';
import caPhe from '../data/ca-phe.json';
import chinhSachGia from '../data/chinh-sach-gia.json';
import capCuaHang from '../data/cap-cua-hang.json';
import luong from '../data/luong.json';
import offline from '../data/offline.json';
import tienDo from '../data/tien-do.json';
import noiThat from '../data/noi-that.json';
import manifest from '../data/manifest.json';
// M3: số liệu từ workbook economy-m3.xlsx.
import khuVuc from '../data/khu-vuc.json';
import thoiTiet from '../data/thoi-tiet.json';
import npc from '../data/npc.json';
import nhiemVu from '../data/nhiem-vu.json';
import thanhTuu from '../data/thanh-tuu.json';
import suuTap from '../data/suu-tap.json';
import cosmetic from '../data/cosmetic.json';
import amThanh from '../data/am-thanh.json';
// M3: trọng số món cà phê.
import trongSoCaPhe from '../data/ca-phe-bo-sung.json';
// M3: nội dung chữ (nháp, user duyệt lô ở phase B).
import noiDungNpcJson from '../data/noi-dung/npc.json';
import hoiThoaiJson from '../data/noi-dung/hoi-thoai.json';
import noiDungNhiemVuJson from '../data/noi-dung/nhiem-vu.json';
import noiDungThanhTuuJson from '../data/noi-dung/thanh-tuu.json';
import noiDungCosmeticJson from '../data/noi-dung/cosmetic.json';
import chinhSachRiengTuJson from '../data/noi-dung/chinh-sach-rieng-tu.json';

function mapTuMang(mang: Array<{ ma: string; giaTri: number }>, ten: string): Map<string, number> {
  const m = new Map<string, number>();
  for (const p of mang) {
    if (m.has(p.ma)) throw new Error(`Trùng mã tham số ${p.ma} trong ${ten}.`);
    m.set(p.ma, p.giaTri);
  }
  return m;
}

/** Tải toàn bộ dữ liệu cân bằng. Ném lỗi nếu thiếu tham số bắt buộc. */
export function taiDuLieu(): DuLieuGame {
  const duLieu: DuLieuGame = {
    thamSo: mapTuMang(thamSo.thamSo, 'tham-so.json'),
    thamSoMoPhong: mapTuMang(thamSoMoPhong.thamSoMoPhong, 'tham-so-mo-phong.json'),
    nhomHang: nhomHang.nhomHang,
    matHang: matHang.matHang,
    kieuKhach: khach.kieuKhach,
    caPhe: {
      nguon: caPhe.nguon,
      phienBan: caPhe.phienBan,
      monCaPhe: caPhe.monCaPhe,
      thongKe: caPhe.thongKe,
    },
    chinhSach: chinhSachGia.chinhSach,
    bangGiaTheoNhom: chinhSachGia.bangTheoNhom,
    capCuaHang: capCuaHang.capCuaHang,
    luong: {
      nguon: luong.nguon,
      phienBan: luong.phienBan,
      thuNgan: luong.thuNgan,
      xepKe: luong.xepKe,
    },
    offline: {
      nguon: offline.nguon,
      phienBan: offline.phienBan,
      capGioThuc: offline.capGioThuc,
      hieuSuat: offline.hieuSuat,
      heSoLuong: offline.heSoLuong,
    },
    tienDo: {
      nguon: tienDo.nguon,
      phienBan: tienDo.phienBan,
      capCuaHang: tienDo.capCuaHang,
      nguoiChoi: tienDo.nguoiChoi,
    },
    noiThat: {
      nguon: noiThat.nguon,
      phienBan: noiThat.phienBan,
      khuVuc: noiThat.khuVuc,
      noiThat: noiThat.noiThat,
    },
    manifest: {
      nguon: manifest.nguon,
      phienBan: manifest.phienBan,
      asset: manifest.asset.map((a) => {
        if (
          a.loai !== 'khach' &&
          a.loai !== 'noi-that' &&
          a.loai !== 'moi-truong' &&
          a.loai !== 'mon'
        ) {
          throw new Error(`Loại asset lạ trong manifest: ${a.id} (${a.loai}).`);
        }
        return a as {
          id: string;
          loai: 'khach' | 'noi-that' | 'moi-truong' | 'mon';
          moTa: string;
          khopVoi: string;
          ghiChuVe: string;
        };
      }),
    },
    khuVuc: {
      nguon: khuVuc.nguon,
      phienBan: khuVuc.phienBan,
      khuVuc: khuVuc.khuVuc,
      luuLuongTheoCap: khuVuc.luuLuongTheoCap,
    },
    thoiTiet: {
      nguon: thoiTiet.nguon,
      phienBan: thoiTiet.phienBan,
      thoiTiet: thoiTiet.thoiTiet,
    },
    npc: {
      nguon: npc.nguon,
      phienBan: npc.phienBan,
      npc: npc.npc.map((n) => {
        if (n.vaiTro !== 'nhaCungCap' && n.vaiTro !== 'khachQuen' && n.vaiTro !== 'hangXom') {
          throw new Error(`Vai trò NPC lạ: ${n.ma} (${n.vaiTro}).`);
        }
        return n as NpcThamSo;
      }),
    },
    nhiemVu: {
      nguon: nhiemVu.nguon,
      phienBan: nhiemVu.phienBan,
      nhiemVuHangNgay: nhiemVu.nhiemVuHangNgay.map((m) => {
        const hopLe = ['banMonNhom', 'phucVuKieuKhach', 'doanhThu', 'banCaPhe'];
        if (!hopLe.includes(m.loaiMucTieu)) {
          throw new Error(`Loại mục tiêu NV lạ: ${m.ma} (${m.loaiMucTieu}).`);
        }
        return m as NhiemVuHangNgay;
      }),
      donLonMau: nhiemVu.donLonMau,
    },
    thanhTuu: {
      nguon: thanhTuu.nguon,
      phienBan: thanhTuu.phienBan,
      thanhTuu: thanhTuu.thanhTuu.map((t) => {
        const hopLe = [
          'tongMonBan',
          'tongLaiGop',
          'capCuaHang',
          'capNguoiChoi',
          'nvHoanThanh',
          'donLonHoanThanh',
          'npcThanThiet5',
          'caPheDaBan',
          'ngayGame',
          'thueNhanVien',
          'khuDaMo',
        ];
        if (!hopLe.includes(t.loaiDieuKien)) {
          throw new Error(`Loại điều kiện thành tựu lạ: ${t.ma} (${t.loaiDieuKien}).`);
        }
        return t as ThanhTuu;
      }),
    },
    suuTap: { nguon: suuTap.nguon, phienBan: suuTap.phienBan, suuTap: suuTap.suuTap },
    cosmetic: {
      nguon: cosmetic.nguon,
      phienBan: cosmetic.phienBan,
      cosmetic: cosmetic.cosmetic.map((c) => {
        if (c.loai !== 'trangTri' && c.loai !== 'dongPhuc') {
          throw new Error(`Loại cosmetic lạ: ${c.ma} (${c.loai}).`);
        }
        return c as Cosmetic;
      }),
    },
    amThanh: {
      nguon: amThanh.nguon,
      phienBan: amThanh.phienBan,
      amThanh: amThanh.amThanh.map((a) => {
        if (a.loai !== 'nhac' && a.loai !== 'sfx') {
          throw new Error(`Loại âm thanh lạ: ${a.id} (${a.loai}).`);
        }
        return a as AmThanhMau;
      }),
    },
    trongSoCaPhe: {
      nguon: trongSoCaPhe.nguon,
      phienBan: trongSoCaPhe.phienBan,
      trongSoCaPhe: trongSoCaPhe.trongSoCaPhe,
    },
    noiDungNpc: (noiDungNpcJson as { npc: NoiDungNpc[] }).npc,
    hoiThoai: (hoiThoaiJson as { hoiThoai: HoiThoai[] }).hoiThoai,
    noiDungNhiemVu: (noiDungNhiemVuJson as { nhiemVu: NoiDungNhiemVu[] }).nhiemVu,
    noiDungDonLon: (noiDungNhiemVuJson as unknown as { donLon: NoiDungNhiemVu[] }).donLon,
    noiDungThanhTuu: (noiDungThanhTuuJson as { thanhTuu: NoiDungThanhTuu[] }).thanhTuu,
    noiDungSuuTap: (noiDungCosmeticJson as unknown as { suuTap: NoiDungSuuTap[] }).suuTap,
    noiDungCosmetic: (noiDungCosmeticJson as unknown as { cosmetic: NoiDungCosmetic[] }).cosmetic,
    chinhSachRiengTu: (
      chinhSachRiengTuJson as unknown as {
        chinhSachRiengTu: { muc: MucChinhSachRiengTu[] };
      }
    ).chinhSachRiengTu.muc,
  };
  // Kiểm tra nhanh các tham số mô phỏng bắt buộc của M1+M3.
  for (const ma of [
    'M01',
    'M02',
    'M03',
    'M04',
    'M05',
    'M06',
    'M07',
    'M08',
    'M09',
    'M10',
    'M11',
    'M12',
    'M13',
    'M14',
    'M15',
    'M16',
    'M17',
    'M18',
  ]) {
    if (!duLieu.thamSoMoPhong.has(ma)) {
      throw new Error(`Thiếu tham số mô phỏng ${ma} — chạy "npm run data:export".`);
    }
  }
  return duLieu;
}

/** Đọc tham số Pxx. Ném lỗi nếu thiếu (fail fast thay vì số mặc định bịa). */
export function layThamSo(duLieu: DuLieuGame, ma: string): number {
  const v = duLieu.thamSo.get(ma);
  if (v === undefined) throw new Error(`Thiếu tham số ${ma} trong dữ liệu.`);
  return v;
}

/** Đọc tham số mô phỏng Mxx. */
export function layThamSoMoPhong(duLieu: DuLieuGame, ma: string): number {
  const v = duLieu.thamSoMoPhong.get(ma);
  if (v === undefined) throw new Error(`Thiếu tham số mô phỏng ${ma} trong dữ liệu.`);
  return v;
}
